// @ts-strict-ignore
import React from "react";

import * as Sentry from "@sentry/browser";

import { Box, FlexBox, Button, BodyText } from "@braze/beacon-core";

import type { ErrorBoundaryContextType } from "@lib/ErrorBoundaryContext";
import { ErrorBoundaryContext } from "@lib/ErrorBoundaryContext";
import datadogLogger from "@lib/datadogLogger";
import { resolveNroPath } from "@lib/datadogLogger/resolveNroPath";
import type { FetchRequestError } from "@lib/errors";
import { logFullstoryEvent } from "@lib/logFullstoryEvent";
import { setRootErrorBoundary } from "@lib/rootErrorBoundary";
import type { RendersUserModal } from "@lib/rootErrorBoundary";

import useI18nNamespace from "@services/useI18nNamespace";

import { FetchErrorModal } from "./FetchErrorModal";
import Torchie from "./Torchie";
import type { ErrorBoundaryProps, ErrorBoundaryState } from "./types";

const ERROR_MESSAGE_KEY = "An unexpected error occurred.";
const ERROR_BUTTON_TEXT = "Reload";
//component is inline for use of localization hooks/ hook-rules and ErrorBoundary's have to be class components.
const ErrorBoundaryText = ({ text }: { text?: string }) => {
  const { t } = useI18nNamespace("error-boundary");
  if (text === ERROR_MESSAGE_KEY) {
    return <>{t("unexpected-error-body-text", "An unexpected error occurred.")}</>;
  }
  if (text === ERROR_BUTTON_TEXT) {
    return <>{t("error-button-reload-text", "Reload")}</>;
  }
};

export class ErrorBoundary
  extends React.Component<ErrorBoundaryProps, ErrorBoundaryState>
  implements RendersUserModal
{
  contextValue: ErrorBoundaryContextType;
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      userError: undefined,
    };
    this.contextValue = {
      triggerFailure: this.triggerFailure,
      logException: this.logException,
    };
    if (props.isRoot) {
      setRootErrorBoundary(this);
    }
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (isError(error)) {
      const errorBoundaryError = new Error(error.message);
      errorBoundaryError.name = `React ErrorBoundary ${error.name}`;
      errorBoundaryError.stack = info.componentStack;

      // Using the `LinkedErrors` integration to link the errors together.
      setCause(error, errorBoundaryError);
    }
    this.logException(error, info, true);
    this.reportRenderCrashToRum(error, info);
  }

  logException = (error: Error, info?: React.ErrorInfo, logToFullstory?: boolean) => {
    if (process.env.NODE_ENV === "development") {
      console.error(error);
    }

    const { featureId, team } = this.props;
    Sentry.withScope((scope) => {
      scope.setTag("featureId", featureId);
      scope.setTag("team", team);
      if (info && info.componentStack) {
        scope.setContext("react", { componentStack: info.componentStack });
      }
      Sentry.captureException(error);
    });

    if (logToFullstory) {
      logFullstoryEvent("error boundary", {
        error_name: error.name,
        error_message: error.message,
        feature_id: featureId,
        team,
      });
    }
  };

  // DD RUM context attributes are capped; 2000 chars keeps us well under the limit
  // while preserving the most useful part of the React component stack (top ~10 frames).
  private static readonly COMPONENT_STACK_MAX_CHARS = 2000;

  reportRenderCrashToRum = (error: Error, info?: React.ErrorInfo) => {
    const { featureId, team } = this.props;
    const sourceUrl = window.location.pathname;
    const componentStack = info?.componentStack?.slice(0, ErrorBoundary.COMPONENT_STACK_MAX_CHARS) ?? null;

    // Use a stable synthetic title so all render crashes share a single Datadog RUM
    // error group (consistent with DashboardApiStatusWrapper and Handle404). The real
    // error's stack is preserved on the synthetic error so DD RUM still renders
    // source-mapped frames; the original name and message go into context attributes
    // for filtering and drilling down.
    const rumError = new Error("React component render crash");
    rumError.stack = error.stack;

    datadogLogger.rumError(rumError, {
      component: "ErrorBoundary",
      feature_id: featureId,
      team,
      nro_path: resolveNroPath(sourceUrl),
      source_url: sourceUrl,
      error_name: error.name,
      error_message: error.message,
      component_stack: componentStack,
    });
  };

  triggerFailure = (error: Error) => {
    this.logException(error, undefined, true);
    this.reportRenderCrashToRum(error);
    this.setState({ hasError: true });
  };

  renderUserModal = (userError: FetchRequestError) => {
    this.setState({ userError });
  };

  onClickReload = () => {
    const { canReloadComponent, canReloadPage, onBeforeReload = () => {} } = this.props;

    onBeforeReload();

    if (canReloadComponent) {
      this.setState({ hasError: false });
    } else if (canReloadPage) {
      window.location.replace("/");
    }
  };

  render() {
    const { ButtonComponent, canReloadComponent, canReloadPage, hidePickUpWhereYouLeftOffError } = this.props;
    const { hasError, userError } = this.state;

    // If the current path has a broken default state, refreshing the page will just lead to
    // another error. Instead, navigate to "/".
    return (
      <ErrorBoundaryContext.Provider value={this.contextValue}>
        {hasError && !hidePickUpWhereYouLeftOffError && (
          <FlexBox p="xxl" justifyContent="center" alignItems="center" direction="column">
            <Box mb="sm">
              <Torchie />
            </Box>
            <Box mb="lg">
              <BodyText fontWeight="bold">
                <ErrorBoundaryText text={ERROR_MESSAGE_KEY} />
              </BodyText>
            </Box>
            {(canReloadComponent || canReloadPage) && (
              <Box>
                {ButtonComponent ? (
                  <ButtonComponent onClick={this.onClickReload} />
                ) : (
                  <Button variant="secondary" onClick={this.onClickReload}>
                    <ErrorBoundaryText text={ERROR_BUTTON_TEXT} />
                  </Button>
                )}
              </Box>
            )}
          </FlexBox>
        )}
        {!hasError && this.props.children}
        <FetchErrorModal
          error={userError}
          ButtonComponent={ButtonComponent}
          closeModal={() => {
            this.setState({ userError: undefined });
          }}
        />
      </ErrorBoundaryContext.Provider>
    );
  }
}

export default ErrorBoundary;

function setCause(error: Error, cause: Error): void {
  const seenErrors = new WeakMap<Error, boolean>();

  function recurse(error: Error, cause: Error): void {
    // If we've already seen the error, there is a recursive loop somewhere in the error's
    // cause chain. Let's just bail out then to prevent a stack overflow.
    if (seenErrors.has(error)) {
      return;
    }
    if (error.hasOwnProperty("cause") && error.cause instanceof Error) {
      seenErrors.set(error, true);
      return recurse(error.cause, cause);
    }
    error.cause = cause;
  }

  recurse(error, cause);
}

function isError(wat: unknown): wat is Error {
  switch (Object.prototype.toString.call(wat)) {
    case "[object Error]":
    case "[object Exception]":
    case "[object DOMException]":
      return true;
    default:
      return isInstanceOf(wat, Error);
  }
}

function isInstanceOf(wat: any, base: any): boolean {
  try {
    return wat instanceof base;
  } catch (_e) {
    return false;
  }
}
